import { type FormEvent, useEffect, useState } from "react";
import { Link } from "react-router";
import { useAuth } from "../auth/AuthContext";
import { initialPasswordError } from "../../invite-flow.js";
import { AuthLayout } from "../components/AuthLayout";
import { signOutEverywhere, updatePassword } from "../lib/auth";
import { getSupabaseClient } from "../lib/supabase";

type RecoveryState = "loading" | "ready" | "failure" | "signout-failure";

const INVALID_RECOVERY_LINK =
  "De resetlink is ongeldig of verlopen. Vraag een nieuwe resetlink aan.";

export function ResetPasswordPage() {
  const {
    loading: authLoading,
    passwordRecovery,
    clearPasswordRecovery,
  } = useAuth();
  const [state, setState] = useState<RecoveryState>("loading");
  const [fatalError, setFatalError] = useState("");
  const [formError, setFormError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!passwordRecovery) {
      setFatalError(INVALID_RECOVERY_LINK);
      setState("failure");
      return;
    }

    let active = true;
    void getSupabaseClient().auth.getUser().then(({ data, error }) => {
      if (!active) return;
      if (error || !data.user) {
        clearPasswordRecovery();
        setFatalError(INVALID_RECOVERY_LINK);
        setState("failure");
        return;
      }
      setState("ready");
    }).catch(() => {
      if (!active) return;
      clearPasswordRecovery();
      setFatalError(INVALID_RECOVERY_LINK);
      setState("failure");
    });
    return () => {
      active = false;
    };
  }, [authLoading, clearPasswordRecovery, passwordRecovery]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError("");
    const validationError = initialPasswordError(password, confirmation);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setBusy(true);
    try {
      await updatePassword(password);
    } catch {
      setFormError(
        "Het wachtwoord kon niet worden gewijzigd. De resetlink kan verlopen zijn. Vraag zo nodig een nieuwe aan.",
      );
      setBusy(false);
      return;
    }

    try {
      await signOutEverywhere();
      globalThis.location.assign("/login/?password=changed");
    } catch (error) {
      console.error("Global sign-out after password reset failed", error);
      setFatalError(
        "Je wachtwoord is gewijzigd, maar bestaande sessies konden niet worden afgesloten. Sluit de app en neem contact op met beheer.",
      );
      setState("signout-failure");
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      {state === "loading" && (
        <section>
          <h1>Resetlink controleren</h1>
          <p>Een ogenblik.</p>
        </section>
      )}
      {state === "failure" && (
        <section>
          <h1>Resetlink niet beschikbaar</h1>
          <p className="error" role="alert">{fatalError}</p>
          <p>
            <Link to="/forgot-password/">Nieuwe resetlink aanvragen</Link>
          </p>
        </section>
      )}
      {state === "signout-failure" && (
        <section>
          <h1>Wachtwoord gewijzigd</h1>
          <p className="error" role="alert">{fatalError}</p>
        </section>
      )}
      {state === "ready" && (
        <section>
          <h1>Nieuw wachtwoord instellen</h1>
          <p>
            Gebruik minimaal 12 tekens met hoofdletters, kleine letters, cijfers en een speciaal
            teken.
          </p>
          <form onSubmit={handleSubmit}>
            <label htmlFor="recovery-password">Nieuw wachtwoord</label>
            <input
              id="recovery-password"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              disabled={busy}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <label htmlFor="recovery-confirmation">Herhaal wachtwoord</label>
            <input
              id="recovery-confirmation"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              disabled={busy}
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
            <button type="submit" disabled={busy}>
              {busy ? "Wijzigen..." : "Wachtwoord wijzigen"}
            </button>
          </form>
          {formError && <p className="error" role="alert">{formError}</p>}
        </section>
      )}
    </AuthLayout>
  );
}
